import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';

/**
 * Feature Flags client (Phase 7A #5)
 *
 * Wraps the app with FeatureFlagsProvider (in App.js). Any component can then:
 *
 *   const { enabled } = useFeature('compare_listings');
 *   if (!enabled) return null;
 *
 * Or check multiple flags:
 *
 *   const flags = useFeatureFlags();
 *   flags.saved_searches === true
 *
 * Flags are fetched once at mount + on auth change. Cached until a manual refresh
 * (or the user logs in/out).
 */

const FeatureFlagsContext = createContext({ flags: {}, ready: false, refresh: () => {} });

export const FeatureFlagsProvider = ({ children }) => {
  const [flags, setFlags] = useState({});
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/features');
      setFlags(data || {});
    } catch {
      // Fail-open: on error, assume all flags disabled (safest default)
      setFlags({});
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <FeatureFlagsContext.Provider value={{ flags, ready, refresh }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => useContext(FeatureFlagsContext).flags;

/** Convenience hook for a single flag. Returns `{ enabled: boolean, ready: boolean }`. */
export const useFeature = (key) => {
  const ctx = useContext(FeatureFlagsContext);
  return { enabled: !!ctx.flags?.[key], ready: ctx.ready };
};

/** Ready-made <IfFeatureEnabled> wrapper for cleaner JSX. */
export const IfFeatureEnabled = ({ feature, fallback = null, children }) => {
  const { enabled, ready } = useFeature(feature);
  if (!ready) return null;
  return enabled ? children : fallback;
};

export default FeatureFlagsProvider;
